import type { ReviewPlan } from '@aj/core';
import { Button, Card, HStack, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { LogForm } from '../../components/LogForm.js';
import { useI18n, useT } from '../../providers/I18nProvider.js';

/**
 * What to revise today (§48 #10) — the differentiator, as one line.
 *
 * Spaced retrieval (d = 0.54) and interleaving (g = 0.42) delivered without a
 * deck, a card, or an opt-in: the student studies, and this appears when
 * something is due. §50's lazy path at day 9 is exactly this card being the
 * obvious thing to tap.
 *
 * Three things it deliberately does not do:
 *
 *  • **It never appears with nothing to say.** A card that always says
 *    something teaches students to stop reading it, so `reviewPlan` returns
 *    null on the quiet days and this renders nothing at all.
 *  • **It never shows the backlog.** Someone back after two weeks has thirty
 *    due topics; the cap keeps the rest silent rather than turning the screen
 *    into a debt statement (§45).
 *  • **It never counts a streak of reviews.** That would turn the one
 *    genuinely evidence-backed feature into a scoreboard.
 */
export type ReviewCardProps = { plan: ReviewPlan; className?: string; padding?: 4 | 5 };

export function ReviewCard({ plan, className, padding = 4 }: ReviewCardProps) {
  const t = useT();
  const i18n = useI18n();
  const [revising, setRevising] = useState(false);

  const topics = plan.items.map((item) => item.topic);
  const worst = plan.items[0];

  return (
    <Card padding={padding} className={className}>
      <VStack gap={3}>
        <HStack gap={3} align="baseline" justify="between">
          <Text variant="overline" tone="subtle">
            {t('review.title')}
          </Text>
          {/* Lateness is a fact about the material, not about the student —
              stated flatly, in the same tone as everything else. */}
          <Text variant="caption" tone="subtle">
            {worst && worst.overdueDays > 0
              ? t('review.overdue', { count: worst.overdueDays })
              : t('review.today')}
          </Text>
        </HStack>

        {/* One sentence when the whole sitting is one subject — that IS the
            interleaving, and reading it as one thing is what makes a student
            do it in one sitting. */}
        <Text variant="body" tone="ink">
          {plan.track
            ? t('review.oneTrack', { track: plan.track, topics: i18n.formatList(topics) })
            : i18n.formatList(topics)}
        </Text>

        <Text variant="caption" tone="muted" numeric>
          {t('review.estimate', { minutes: i18n.formatDuration(plan.minutes) })}
        </Text>

        {revising ? (
          // Pre-filled and marked as a review. This is the one place `kind`
          // is set without asking — which is precisely what lets ordinary
          // logging stay at three taps and never pose the question.
          <LogForm
            kind="review"
            {...(plan.track ? { track: plan.track } : {})}
            {...(worst ? { intention: worst.topic } : {})}
            onDone={() => setRevising(false)}
          />
        ) : (
          <HStack gap={3} align="center" className="flex-wrap">
            <Button icon="check" onClick={() => setRevising(true)}>
              {t('review.start')}
            </Button>
            {plan.hiddenCount > 0 && (
              <Text variant="caption" tone="subtle">
                {t('review.more', { count: plan.hiddenCount })}
              </Text>
            )}
          </HStack>
        )}
      </VStack>
    </Card>
  );
}
